<template>
    <component
        :is="embedded ? 'div' : 'main'"
        :class="embedded
            ? 'user-account-edit-embedded flex-1 min-h-0 overflow-y-auto focus:outline-none'
            : 'flex-1 relative overflow-y-auto py-6 focus:outline-none'"
        tabindex="0"
    >
        <div v-if="embedded" class="user-account-edit-toolbar">
            <button
                aria-label="Benutzerbearbeitung schließen"
                class="user-account-edit-close"
                title="Zurück"
                type="button"
                @click="$emit('close')"
            >
                <svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-right"/>
            </button>
            <span class="user-account-edit-toolbar-title">Anwenderdetails</span>
            <button
                :aria-busy="loadingState === 'all'"
                class="user-account-edit-save btn-stage-primary"
                type="button"
                @click="saveUser('all')"
            >
                {{ $t('Save') }}
            </button>
        </div>
        <form novalidate @submit.prevent="saveUser('all')">
            <div v-if="!embedded" class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5 flex items-center justify-between gap-4">
                <h1 class="py-0.5 text-2xl font-semibold text-gray-900">Anwenderdetails</h1>
                <button class="btn-stage-primary" type="submit">{{ $t('Save') }}</button>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-sm">
                    <loading :status="isSectionLoading('details')"/>
                    <div class="user-account-edit-section bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">Anwenderkonto</h3>
                            <p class="stage-form-help-text mt-1 text-xs leading-5">
                                Stammdaten und Kontaktdaten des Benutzerkontos.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="space-y-6">
                                <section>
                                    <h4 class="text-sm font-semibold text-gray-900">Persönliche Angaben</h4>
                                    <div class="mt-3 grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-6">
                                        <div class="md:col-span-2">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="salutation">{{ $t('Salutation') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <select
                                                    id="salutation"
                                                    v-model="user.salutation"
                                                    class="block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                >
                                                    <option :value="null">{{ $t('Select salutation') }}</option>
                                                    <option value="mr">{{ $t('Mr') }}</option>
                                                    <option value="ms">{{ $t('Ms') }}</option>
                                                    <option value="diverse">{{ $t('Diverse') }}</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div class="md:col-span-4">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="name">{{ $t('Name') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="name"
                                                    v-model="user.name"
                                                    :placeholder="$t('Name')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="255"
                                                    required
                                                >
                                            </div>
                                        </div>
                                        <div class="md:col-span-6">
                                            <label class="block text-sm leading-5 font-medium text-gray-700">{{ $t('Avatar') }}</label>
                                            <div class="mt-2 flex items-center select-none">
                                                <div class="avatar-editor relative inline-block h-12 w-12 overflow-hidden bg-gray-100">
                                                    <button
                                                        v-show="hasCustomAvatar && !avatarRemoved"
                                                        class="avatar-editor-remove absolute top-0 right-0 items-center p-0.5 rounded-full leading-4 bg-red-600 text-white cursor-pointer"
                                                        type="button"
                                                        @click="removeAvatar"
                                                    >
                                                        <svg-vue class="h-4 w-4 p-px" icon="font-awesome.times-light"></svg-vue>
                                                    </button>
                                                    <img
                                                        :src="avatarPreviewUrl"
                                                        alt="User avatar"
                                                        class="h-full w-full text-gray-300 rounded-full"
                                                    />
                                                </div>
                                                <span class="ml-5 rounded shadow-sm">
                                                    <input ref="changeAvatar" accept=".png,.jpg,.jpeg" hidden type="file" @change="changeAvatar($event)">
                                                    <button
                                                        class="py-2 px-3 border border-gray-300 rounded text-sm leading-4 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
                                                        type="button"
                                                        @click="selectAvatar"
                                                    >
                                                        {{ $t('Change') }}
                                                    </button>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section class="border-t border-ultralight pt-5">
                                    <h4 class="text-sm font-semibold text-gray-900">Erreichbarkeit</h4>
                                    <div class="mt-3 grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
                                        <div class="md:col-span-2">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="email">{{ $t('Email') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="email"
                                                    v-model="user.email"
                                                    :placeholder="$t('Email')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="255"
                                                    required
                                                    type="email"
                                                >
                                            </div>
                                        </div>
                                        <div>
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="phone_landline">{{ $t('Landline phone') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="phone_landline"
                                                    v-model="user.phone_landline"
                                                    :placeholder="$t('Landline phone')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="50"
                                                    pattern="\+?[0-9][0-9 ()/.\-]{2,49}"
                                                    title="Bitte geben Sie eine gültige Telefonnummer ein."
                                                    type="tel"
                                                >
                                            </div>
                                        </div>
                                        <div>
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="phone_mobile">{{ $t('Mobile phone') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="phone_mobile"
                                                    v-model="user.phone_mobile"
                                                    :placeholder="$t('Mobile phone')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="50"
                                                    pattern="\+?[0-9][0-9 ()/.\-]{2,49}"
                                                    title="Bitte geben Sie eine gültige Telefonnummer ein."
                                                    type="tel"
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section class="border-t border-ultralight pt-5">
                                    <h4 class="text-sm font-semibold text-gray-900">Social Media</h4>
                                    <div class="mt-3 grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
                                        <div>
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="linkedin_url">LinkedIn</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="linkedin_url"
                                                    v-model.trim="user.linkedin_url"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="255"
                                                    placeholder="https://www.linkedin.com/in/..."
                                                    type="url"
                                                >
                                            </div>
                                        </div>
                                        <div>
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="xing_url">XING</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="xing_url"
                                                    v-model.trim="user.xing_url"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="255"
                                                    placeholder="https://www.xing.com/profile/..."
                                                    type="url"
                                                >
                                            </div>
                                        </div>
                                        <div>
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="instagram_url">Instagram</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="instagram_url"
                                                    v-model.trim="user.instagram_url"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="255"
                                                    placeholder="https://www.instagram.com/..."
                                                    type="url"
                                                >
                                            </div>
                                        </div>
                                        <div>
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="facebook_url">Facebook</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="facebook_url"
                                                    v-model.trim="user.facebook_url"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="255"
                                                    placeholder="https://www.facebook.com/..."
                                                    type="url"
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section class="border-t border-ultralight pt-5">
                                    <h4 class="text-sm font-semibold text-gray-900">Anschrift</h4>
                                    <div class="mt-3 grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-6">
                                        <div class="md:col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="street">{{ $t('Street') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="street"
                                                    v-model="user.street"
                                                    :placeholder="$t('Street')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="255"
                                                >
                                            </div>
                                        </div>
                                        <div class="md:col-span-1">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="house_number">{{ $t('House number') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="house_number"
                                                    v-model="user.house_number"
                                                    placeholder="Nr"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="32"
                                                >
                                            </div>
                                        </div>
                                        <div class="md:col-span-2">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="postal_code">{{ $t('Postal code') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="postal_code"
                                                    v-model="user.postal_code"
                                                    :placeholder="$t('Postal code')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="20"
                                                    pattern="[A-Za-z0-9][A-Za-z0-9 \-]{1,19}"
                                                    title="Bitte geben Sie eine gültige Postleitzahl ein."
                                                >
                                            </div>
                                        </div>
                                        <div class="md:col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="city">{{ $t('City') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="city"
                                                    v-model="user.city"
                                                    :placeholder="$t('City')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="120"
                                                >
                                            </div>
                                        </div>
                                        <div class="md:col-span-3">
                                            <label class="block text-sm font-medium leading-5 text-gray-700" for="country">{{ $t('Country') }}</label>
                                            <div class="mt-1 relative rounded shadow-sm">
                                                <input
                                                    id="country"
                                                    v-model="user.country"
                                                    :placeholder="$t('Country')"
                                                    class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                    maxlength="120"
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
        <form v-if="loadingState !== 'initial' && notificationSettings.length > 0" @submit.prevent="saveUser('all')">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-sm">
                    <loading :status="isSectionLoading('notification_settings')"/>
                    <div class="user-account-edit-section bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Notification settings') }}</h3>
                            <p class="stage-form-help-text mt-1 text-xs leading-5">
                                Systembenachrichtigungen, die für dieses Benutzerkonto per E-Mail versendet werden.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="form-selection-panel">
                                <label
                                    v-for="item in notificationSettings"
                                    :key="'admin-notification-setting-' + item.key"
                                    :for="'admin-notification-setting-checkbox-' + item.key"
                                    class="form-selection-option grid cursor-pointer grid-cols-12 items-center gap-3 px-3 py-2"
                                >
                                    <div class="col-span-3 min-w-0 truncate text-sm font-normal text-gray-800">
                                        {{ translateTemplateLabel(item.module_translation_key, item.module) }}
                                    </div>
                                    <div class="col-span-8 min-w-0 truncate text-sm font-normal text-gray-500">
                                        {{ translateTemplateLabel(item.name_translation_key, item.name) }}
                                    </div>
                                    <input
                                        :id="'admin-notification-setting-checkbox-' + item.key"
                                        v-model="item.enabled"
                                        class="form-checkbox col-span-1 h-4 w-4 justify-self-end text-gray-700"
                                        type="checkbox"
                                    >
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
        <div v-if="loadingState !== 'initial' && !user.is_primary_admin" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div v-if="showPasswordLoginCard" class="mt-6 shadow sm:rounded-sm">
                <form @submit.prevent="saveUser('all')">
                    <loading :status="isSectionLoading('password_login')"/>
                    <div class="user-account-edit-section bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Password login') }}</h3>
                            <p class="stage-form-help-text mt-1 text-xs leading-5">
                                Zusätzliche Aktivierung der Passwort-Anmeldung für dieses Benutzerkonto.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <div class="mt-1 flex rounded">
                                        <input-switch
                                            id="password_login_enabled"
                                            v-model="user.password_login_enabled"
                                            :disabled="user.is_primary_admin"
                                            :disabled-label="$t('Activate password login')"
                                            :enabled-label="$t('Activate password login')"
                                        ></input-switch>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
            <div v-if="showPasswordChangeCard" class="mt-6 shadow sm:rounded-sm">
                <form @submit.prevent="changePassword">
                    <loading :status="passwordLoading"/>
                    <div class="user-account-edit-section bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-3">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Change password') }}</h3>
                        </div>
                        <div class="mt-5 md:col-span-3">
                            <div class="flex flex-col gap-4 md:flex-row md:items-end">
                                <div class="min-w-0 flex-1">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="password">{{ $t('New password') }}</label>
                                    <div class="mt-1 relative rounded shadow-sm">
                                        <input
                                            id="password"
                                            v-model="user.password"
                                            :placeholder="$t('New password')"
                                            autocomplete="new-password"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            type="password"
                                        >
                                    </div>
                                </div>
                                <div class="min-w-0 flex-1">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="password_confirmation">{{ $t('Confirm password') }}</label>
                                    <div class="mt-1 relative rounded shadow-sm">
                                        <input
                                            id="password_confirmation"
                                            v-model="user.password_confirmation"
                                            :placeholder="$t('Confirm password')"
                                            autocomplete="new-password"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            type="password"
                                        >
                                    </div>
                                </div>
                                <div class="flex flex-none items-end">
                                    <button class="btn btn-white whitespace-nowrap" type="submit">
                                        {{ $t('Change password') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
        <form @submit.prevent="saveUser('all')">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-sm">
                    <loading :status="isSectionLoading('settings')"/>
                    <div class="user-account-edit-section bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('User settings') }}</h3>
                            <p class="stage-form-help-text mt-1 text-xs leading-5">
                                Rollenzuordnungen, Zugriffsrechte und verfügbare Module des Benutzerkontos.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <span class="block text-sm font-medium leading-5 text-gray-700">{{ $t('Role') }}</span>
                                    <div class="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-gray-700 sm:text-sm sm:leading-5">
                                        {{ selectedRole ? selectedRole.name : '-' }}
                                    </div>
                                    <p class="mt-2 text-xs leading-5 text-gray-500">
                                        {{ $t('The user role cannot be edited after the account has been created.') }}
                                    </p>
                                </div>
                                <user-company-assignment
                                    v-model:company-id="user.company_id"
                                    v-model:company-ids="user.company_ids"
                                    v-model:company-assignments="user.company_assignments"
                                    :companies="companies"
                                    :role-group="selectedRoleSlug"
                                />
                                <div v-if="isProjectAssignableRoleSelected" class="col-span-3">
                                    <h4 class="text-sm font-semibold text-gray-700">{{ $t('Projects') }}</h4>
                                    <div class="form-selection-panel mt-2 max-h-56 overflow-y-auto">
                                        <label
                                            v-for="project in projects"
                                            :key="'edit-user-project-' + project.id"
                                            class="form-selection-option flex items-center justify-between px-3 py-2 cursor-pointer"
                                        >
                                            <div class="flex items-center min-w-0">
                                                <input
                                                    v-model="user.project_ids"
                                                    :value="project.id"
                                                    class="form-checkbox h-4 w-4 text-gray-700 mr-3"
                                                    type="checkbox"
                                                >
                                                <img
                                                    :src="project.image_url ? project.image_url : '/images/default/icon.png'"
                                                    :alt="project.title"
                                                    class="h-8 w-8 rounded object-cover border border-gray-200 mr-3 flex-shrink-0"
                                                >
                                                <span class="text-sm text-gray-700 truncate">{{ project.title }}</span>
                                            </div>
                                        </label>
                                        <div v-if="projects.length < 1" class="px-3 py-3 text-sm text-gray-500">
                                            {{ $t('No projects available.') }}
                                        </div>
                                    </div>
                                </div>
                                <user-system-feature-settings
                                    v-if="showSystemFeatureSettings"
                                    :role-group="selectedRoleSlug"
                                    :value="user"
                                    class="col-span-3"
                                    @change="updateSystemFeature"
                                />
                                <div class="col-span-3">
                                    <h4 class="text-sm font-semibold text-gray-700">
                                        Kontakt im Bereich Beziehungen anzeigen
                                    </h4>
                                    <p class="mt-1 text-xs leading-5 text-gray-500">
                                        <template v-if="isCustomerRoleSelected">
                                            Angehörige des eigenen Unternehmens sind immer sichtbar. Für zusätzlich freigegebene Benutzergruppen erscheint der Account nur, wenn außerdem eine direkte Verknüpfung im System besteht, zum Beispiel über ein gemeinsames Unternehmen oder eine aktive Ticketteilnahme.
                                        </template>
                                        <template v-else>
                                            Die Checkbox erteilt nur die grundsätzliche Freigabe. Sichtbar wird der Account für die ausgewählte Benutzergruppe erst, wenn zusätzlich eine direkte Verknüpfung im System besteht, zum Beispiel über ein gemeinsames Unternehmen oder eine aktive Ticketteilnahme.
                                        </template>
                                    </p>
                                    <div class="form-selection-panel mt-2 overflow-hidden">
                                        <label v-if="!isCustomerRoleSelected" class="form-selection-option flex items-center px-3 py-2 cursor-pointer">
                                            <input
                                                v-model="user.relationships_contact_visible_to_customers"
                                                class="form-checkbox h-4 w-4 text-gray-700 mr-3"
                                                type="checkbox"
                                            >
                                            <span class="text-sm text-gray-700">Kunden</span>
                                        </label>
                                        <label class="form-selection-option flex items-center px-3 py-2 cursor-pointer">
                                            <input
                                                v-model="user.relationships_contact_visible_to_employees"
                                                class="form-checkbox h-4 w-4 text-gray-700 mr-3"
                                                type="checkbox"
                                            >
                                            <span class="text-sm text-gray-700">Mitarbeiter</span>
                                        </label>
                                        <label class="form-selection-option flex items-center px-3 py-2 cursor-pointer">
                                            <input
                                                v-model="user.relationships_contact_visible_to_service_providers"
                                                class="form-checkbox h-4 w-4 text-gray-700 mr-3"
                                                type="checkbox"
                                            >
                                            <span class="text-sm text-gray-700">Mitarbeitende Dienstleister</span>
                                        </label>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="status">{{ $t('Status') }}</label>
                                    <div class="mt-1 flex rounded">
                                        <input-switch
                                            id="status"
                                            v-model="user.status"
                                            :disabled-label="$t('The user is disabled')"
                                            :enabled-label="$t('The user is activated')"
                                        ></input-switch>
                                    </div>
                                    <div class="mt-2 relative text-xs text-gray-500">
                                        Bei Deaktivierung bleibt das Benutzerkonto im System erhalten. Eine Anmeldung ist erst nach erneuter Aktivierung möglich.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
        <div v-if="loadingState !== 'initial' && !user.is_primary_admin" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="mt-6 shadow sm:rounded-sm">
                <form @submit.prevent="saveUser('all')">
                    <loading :status="isSectionLoading('ai')"/>
                    <div class="user-account-edit-section bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('AI settings') }}</h3>
                            <p class="stage-form-help-text mt-1 text-xs leading-5">
                                KI-Funktionen sind nur verfügbar, wenn diese Einstellung und die globale KI-Einstellung aktiv sind.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="flex items-center">
                                <input-switch
                                    id="ai_enabled"
                                    v-model="user.ai_enabled"
                                ></input-switch>
                                <span class="ml-3 text-sm text-gray-700">{{ $t('Enable AI functions') }}</span>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
        <form v-if="showStorageLimitCard" @submit.prevent="saveUser('all')">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-sm">
                    <loading :status="isSectionLoading('storage_limit')"/>
                    <div class="user-account-edit-section bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Storage limit') }}</h3>
                            <p class="stage-form-help-text mt-1 text-xs leading-5">
                                Speicherlimit im Dateimanager für dieses Benutzerkonto.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-1 gap-6">
                                <div>
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="file_manager_storage_limit_mb">
                                        {{ $t('Storage limit') }}
                                    </label>
                                    <select
                                        id="file_manager_storage_limit_mb"
                                        v-model="user.file_manager_storage_limit_mb"
                                        class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                    >
                                        <option
                                            v-for="option in storageLimitOptions"
                                            :key="'storage-limit-option-' + String(option.id)"
                                            :value="option.id"
                                        >
                                            {{ option.name }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="mt-6 shadow sm:rounded-sm">
                <form @submit.prevent="saveUser('all')">
                    <loading :status="isSectionLoading('test_account')"/>
                    <div class="user-account-edit-section bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Test account settings') }}</h3>
                            <p class="stage-form-help-text mt-1 text-xs leading-5">
                                Testaccounts können über die Entwickleroptionen in Auswahllisten ausgeblendet werden.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <div v-if="user.is_primary_admin" class="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                        Das primäre Administratorkonto bleibt immer als reguläres Konto sichtbar.
                                    </div>
                                    <div class="mt-1 flex rounded">
                                        <input-switch
                                            id="is_test_account"
                                            v-model="user.is_test_account"
                                            :disabled="user.is_primary_admin"
                                            :disabled-label="$t('mark as test account')"
                                            :enabled-label="$t('mark as test account')"
                                        ></input-switch>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
        <div class="user-account-editor-footer-wrap max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <footer class="user-account-editor-footer mt-6">
                <span v-if="lastUpdatedAt" class="user-account-editor-updated text-xs text-gray-500">
                    <img
                        v-if="lastUpdatedUser && lastUpdatedUser.avatar"
                        :alt="lastUpdatedUser.name"
                        :src="lastUpdatedUser.avatar"
                        class="user-account-editor-updated-avatar"
                    >
                    <span v-if="lastUpdatedUser">{{ lastUpdatedUser.name }}</span>
                    <span>{{ lastUpdatedUser ? 'am ' : '' }}{{ lastUpdatedAt }}</span>
                </span>
                <button
                    aria-label="Anwender löschen"
                    class="user-account-editor-delete btn-stage-icon-plain"
                    title="Anwender löschen"
                    type="button"
                    @click="openDeleteUserModal"
                >
                    <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"/>
                </button>
            </footer>
        </div>
        <delete-guard-dialog
            :blockers="deleteGuard.blockers"
            :deletable="canDeleteUser"
            :loading="deleteLoading"
            :open="deleteUserModal"
            confirmation-text="Möchten Sie dieses Benutzerkonto wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden."
            title="Anwender löschen"
            @close="deleteUserModal = false"
            @confirm="deleteUser"
        />
    </component>
</template>

<script>
import DeleteGuardDialog from '@/components/modals/delete-guard-dialog.vue';
import UserCompanyAssignment from '@/components/users/user-company-assignment';
import UserSystemFeatureSettings from '@/components/users/user-system-feature-settings';
import {deriveRoleGroup} from "@/utils/access-context";
import {resolveReturnToPath} from "@/utils/list-route-state";
import {isUserSystemFeatureVisible} from '@/utils/user-system-features';
import {
    normalizeCompanyId,
    normalizeCompanyIds,
    normalizeUserCompanyAssignment,
} from '@/utils/user-company-assignment';

export default {
    name: "edit-user",
    components: {DeleteGuardDialog, UserCompanyAssignment, UserSystemFeatureSettings},
    props: {
        embedded: {
            type: Boolean,
            default: false,
        },
    },
    metaInfo() {
        return {
            title: 'Anwenderdetails'
        }
    },
    data() {
        return {
            loadingState: 'initial',
            passwordLoading: false,
            deleteLoading: false,
            deleteUserModal: false,
            userRoles: [],
            companies: [],
            projects: [],
            user: {
                name: null,
                email: null,
                customer_positions_enabled: true,
                customer_timeline_enabled: true,
                customer_file_manager_enabled: true,
                customer_documentations_enabled: true,
                customer_boards_enabled: false,
                customer_relationships_enabled: false,
                relationships_contact_visible_to_customers: false,
                relationships_contact_visible_to_employees: false,
                relationships_contact_visible_to_service_providers: false,
                customer_finances_enabled: false,
                customer_server_monitoring_enabled: false,
                customer_vault_enabled: false,
                customer_cms_enabled: false,
                ai_enabled: true,
                salutation: null,
                country: null,
                city: null,
                postal_code: null,
                street: null,
                house_number: null,
                phone_landline: null,
                phone_mobile: null,
                linkedin_url: null,
                xing_url: null,
                instagram_url: null,
                facebook_url: null,
                file_manager_storage_limit_mb: 500,
                avatar: null,
                gravatar: null,
                avatar_preview: null,
                role_id: null,
                company_id: null,
                company_ids: [],
                company_assignments: [],
                project_ids: [],
                password_login_enabled: false,
                password_login_available: false,
                is_primary_admin: false,
                is_test_account: false,
                status: true,
                password: null,
                password_confirmation: null,
                delete_guard: {
                    deletable: false,
                    message: '',
                    blockers: [],
                },
            },
            savedUser: null,
            notificationSettings: [],
            savedNotificationSettings: [],
            avatarRemoved: false,
        }
    },
    mounted() {
        this.getProjects();
        Promise.all([
            this.getUserRoles(),
            this.getCompanies(),
        ]).then(() => this.getUser());
    },
    computed: {
        usersListPath() {
            return resolveReturnToPath(this.$route.query || {}, '/workspace/admin/users');
        },
        selectedRole() {
            return this.userRoles.find((role) => String(role.id) === String(this.user.role_id)) || null;
        },
        selectedRoleSlug() {
            if (!this.selectedRole) {
                return '';
            }

            return deriveRoleGroup(this.selectedRole);
        },
        isCustomerRoleSelected() {
            return this.selectedRoleSlug === 'customer';
        },
        isAdminRoleSelected() {
            return this.selectedRoleSlug === 'admin';
        },
        isAccountSettingsRoleSelected() {
            if (!this.selectedRole || this.isAdminRoleSelected) {
                return false;
            }

            return ['customer', 'employee', 'service_provider', 'guest'].includes(this.selectedRoleSlug);
        },
        isProjectAssignableRoleSelected() {
            return this.isAccountSettingsRoleSelected;
        },
        showSystemFeatureSettings() {
            return this.isAccountSettingsRoleSelected;
        },
        isGuestRoleSelected() {
            return this.selectedRoleSlug === 'guest';
        },
        isCustomerPositionsFeatureVisibleForRole() {
            return (roleId) => {
                const role = this.userRoles.find((item) => String(item.id) === String(roleId));
                if (!role) {
                    return true;
                }

                return isUserSystemFeatureVisible('customer_positions_enabled', deriveRoleGroup(role));
            };
        },
        isCustomerTimelineFeatureVisibleForRole() {
            return (roleId) => {
                const role = this.userRoles.find((item) => String(item.id) === String(roleId));
                if (!role) {
                    return true;
                }

                return isUserSystemFeatureVisible('customer_timeline_enabled', deriveRoleGroup(role));
            };
        },
        isCustomerFinancesFeatureVisibleForRole() {
            return (roleId) => {
                const role = this.userRoles.find((item) => String(item.id) === String(roleId));
                if (!role) {
                    return true;
                }

                return isUserSystemFeatureVisible('customer_finances_enabled', deriveRoleGroup(role));
            };
        },
        isBoardsFeatureVisibleForRole() {
            return (roleId) => {
                const role = this.userRoles.find((item) => String(item.id) === String(roleId));
                if (!role) {
                    return true;
                }

                return isUserSystemFeatureVisible('customer_boards_enabled', deriveRoleGroup(role));
            };
        },
        isRelationshipsFeatureVisibleForRole() {
            return (roleId) => {
                const role = this.userRoles.find((item) => String(item.id) === String(roleId));
                if (!role) {
                    return true;
                }

                return isUserSystemFeatureVisible('customer_relationships_enabled', deriveRoleGroup(role));
            };
        },
        isCmsFeatureVisibleForRole() {
            return (roleId) => {
                const role = this.userRoles.find((item) => String(item.id) === String(roleId));
                if (!role) {
                    return false;
                }

                return isUserSystemFeatureVisible('customer_cms_enabled', deriveRoleGroup(role));
            };
        },
        isServiceProviderRoleForRoleId() {
            return (roleId) => {
                const role = this.userRoles.find((item) => String(item.id) === String(roleId));

                return role ? deriveRoleGroup(role) === 'service_provider' : false;
            };
        },
        showStorageLimitCard() {
            return (this.isCustomerRoleSelected || this.isGuestRoleSelected) && !!this.user.customer_file_manager_enabled;
        },
        showPasswordLoginCard() {
            return this.user.is_primary_admin || this.user.password_login_available;
        },
        showPasswordChangeCard() {
            return this.user.is_primary_admin || this.user.password_login_available;
        },
        storageLimitOptions() {
            return [
                {id: 500, name: '500 MB'},
                {id: 1024, name: '1 GB'},
                {id: 10240, name: '10 GB'},
                {id: 51200, name: '50 GB'},
                {id: null, name: this.$t('Unlimited').toString()},
            ];
        },
        hasCustomAvatar() {
            if (this.user.avatar instanceof File) {
                return true;
            }
            if (!this.user.avatar || !this.user.gravatar) {
                return false;
            }

            return String(this.user.avatar) !== String(this.user.gravatar);
        },
        avatarPreviewUrl() {
            if (this.user.avatar_preview) {
                return this.user.avatar_preview;
            }
            if (this.avatarRemoved) {
                return this.user.gravatar;
            }

            return this.user.avatar || this.user.gravatar;
        },
        deleteGuard() {
            return this.user.delete_guard || {
                deletable: false,
                message: 'Das Benutzerkonto kann erst gelöscht werden, wenn alle bestehenden Zuordnungen entfernt wurden.',
                blockers: [],
            };
        },
        canDeleteUser() {
            return !!this.deleteGuard.deletable;
        },
        lastUpdatedAt() {
            if (!this.user.updated_at) {
                return '';
            }

            return this.$momentFormatDateTime(this.user.updated_at).replace(/\s+(\d{1,2}:\d{2})$/, ', $1');
        },
        lastUpdatedUser() {
            return this.user.updated_by || null;
        },
    },
    methods: {
        updateSystemFeature({field, value}) {
            if (!Object.prototype.hasOwnProperty.call(this.user, field)) {
                return;
            }

            this.user[field] = value;
        },
        isSectionLoading(section) {
            return this.loadingState === 'initial'
                || this.loadingState === 'all'
                || this.loadingState === section;
        },
        saveUser(section = 'all') {
            if (this.loadingState !== null) {
                return;
            }

            const self = this;
            self.loadingState = section;
            const formData = section === 'notification_settings'
                ? self.buildNotificationSettingsFormData()
                : self.buildUserFormData(
                    self.buildPayloadUserForSection(section),
                    {
                        section,
                        includeAvatar: section === 'details' || section === 'all',
                    }
                );

            axios.post(
                'api/workspace/admin/users/' + self.$route.params.id,
                formData,
                {headers: {'Content-Type': 'multipart/form-data'}}
            ).then(function (response) {
                self.loadingState = null;
                if (response.data.user) {
                    self.applySaveResponse(response.data.user, section);
                    if (self.embedded) {
                        // Publish the normalized server state so the master list and
                        // detail pane render the same canonical user immediately.
                        self.$emit('updated', self.cloneUserState(self.user));
                    }
                }
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t(
                        response.data.message || 'Data updated correctly'
                    ).toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.loadingState = null;
            });
        },
        getUser() {
            const self = this;
            self.loadingState = 'initial';
            axios.get('api/workspace/admin/users/' + self.$route.params.id).then(function (response) {
                const normalizedUser = self.normalizeUserData(response.data, self.user);
                self.user = normalizedUser;
                self.savedUser = self.cloneUserState(normalizedUser);
                self.notificationSettings = self.normalizeNotificationSettings(response.data.notification_settings);
                self.savedNotificationSettings = self.cloneNotificationSettings(self.notificationSettings);
                self.loadingState = null;
            }).catch(function () {
                self.loadingState = null;
            });
        },
        normalizeUserData(data, base = {}) {
            const roleId = data.role_id ?? base.role_id;
            const isServiceProviderRole = this.isServiceProviderRoleForRoleId(roleId);
            const role = this.userRoles.find((item) => String(item.id) === String(roleId));
            const rawCompanyId = data.company_id ?? base.company_id;
            const rawCompanyIds = data.company_ids ?? base.company_ids;
            const rawCompanyAssignments = data.company_assignments ?? base.company_assignments;
            const normalizedCompanyId = normalizeCompanyId(rawCompanyId);
            const normalizedCompanyIds = normalizeCompanyIds(rawCompanyIds);
            const companyAssignment = role
                ? normalizeUserCompanyAssignment(
                    deriveRoleGroup(role),
                    rawCompanyId,
                    rawCompanyIds,
                    this.companies,
                    rawCompanyAssignments
                )
                : {
                    companyId: normalizedCompanyId,
                    companyIds: normalizedCompanyIds.length > 0
                        ? normalizedCompanyIds
                        : (normalizedCompanyId ? [normalizedCompanyId] : []),
                    companyAssignments: [],
                };

            return {
                ...base,
                ...data,
                company_id: companyAssignment.companyId,
                company_ids: companyAssignment.companyIds,
                company_assignments: companyAssignment.companyAssignments,
                file_manager_storage_limit_mb: this.normalizeStorageLimit(data.file_manager_storage_limit_mb),
                customer_positions_enabled: isServiceProviderRole
                    ? true
                    : (this.isCustomerPositionsFeatureVisibleForRole(roleId)
                        && data.customer_positions_enabled !== null ? !!data.customer_positions_enabled : false),
                customer_timeline_enabled: isServiceProviderRole
                    ? true
                    : (this.isCustomerTimelineFeatureVisibleForRole(roleId)
                        && data.customer_timeline_enabled !== null ? !!data.customer_timeline_enabled : false),
                customer_file_manager_enabled: data.customer_file_manager_enabled === null ? true : !!data.customer_file_manager_enabled,
                customer_documentations_enabled: data.customer_documentations_enabled === null ? true : !!data.customer_documentations_enabled,
                customer_boards_enabled: this.isBoardsFeatureVisibleForRole(roleId)
                    && data.customer_boards_enabled !== null ? !!data.customer_boards_enabled : false,
                customer_relationships_enabled: this.isRelationshipsFeatureVisibleForRole(roleId)
                    && data.customer_relationships_enabled !== null ? !!data.customer_relationships_enabled : false,
                relationships_contact_visible_to_customers: !!data.relationships_contact_visible_to_customers,
                relationships_contact_visible_to_employees: !!data.relationships_contact_visible_to_employees,
                relationships_contact_visible_to_service_providers: !!data.relationships_contact_visible_to_service_providers,
                customer_finances_enabled: isServiceProviderRole
                    ? true
                    : (this.isCustomerFinancesFeatureVisibleForRole(roleId)
                        && data.customer_finances_enabled !== null ? !!data.customer_finances_enabled : false),
                customer_server_monitoring_enabled: data.customer_server_monitoring_enabled === null ? false : !!data.customer_server_monitoring_enabled,
                customer_vault_enabled: data.customer_vault_enabled === null ? false : !!data.customer_vault_enabled,
                customer_cms_enabled: this.isCmsFeatureVisibleForRole(roleId)
                    && data.customer_cms_enabled !== null ? !!data.customer_cms_enabled : false,
                ai_enabled: data.ai_enabled === null ? true : !!data.ai_enabled,
                avatar: data.avatar,
                gravatar: data.gravatar,
                avatar_preview: null,
                project_ids: this.normalizeProjectIds(data.project_ids),
                password_login_enabled: !!data.password_login_enabled,
                password_login_available: !!data.password_login_available,
                is_primary_admin: !!data.is_primary_admin,
                is_test_account: !!data.is_test_account,
                delete_guard: {
                    deletable: !!(data.delete_guard && data.delete_guard.deletable),
                    message: data.delete_guard && data.delete_guard.message
                        ? data.delete_guard.message
                        : 'Das Benutzerkonto kann erst gelöscht werden, wenn alle bestehenden Zuordnungen entfernt wurden.',
                    blockers: Array.isArray(data.delete_guard && data.delete_guard.blockers)
                        ? data.delete_guard.blockers
                        : [],
                },
                password: null,
                password_confirmation: null,
            };
        },
        applySaveResponse(data, section) {
            const normalizedSavedUser = this.normalizeUserData(data, this.savedUser || this.user);
            if (Array.isArray(data.notification_settings)) {
                this.notificationSettings = this.normalizeNotificationSettings(data.notification_settings);
                this.savedNotificationSettings = this.cloneNotificationSettings(this.notificationSettings);
            }
            if (!this.savedUser) {
                this.savedUser = this.cloneUserState(normalizedSavedUser);
            }
            if (section === 'all') {
                this.user = normalizedSavedUser;
                this.savedUser = this.cloneUserState(normalizedSavedUser);
            } else {
                const fields = this.getSectionFields(section);
                fields.forEach((field) => {
                    this.savedUser[field] = this.cloneFieldValue(normalizedSavedUser[field]);
                    this.user[field] = this.cloneFieldValue(normalizedSavedUser[field]);
                });
            }

            if (section === 'details' || section === 'all') {
                this.avatarRemoved = false;
                this.user.avatar_preview = null;
                this.savedUser.avatar_preview = null;
                if (this.$refs.changeAvatar) {
                    this.$refs.changeAvatar.value = '';
                }
            }
        },
        buildPayloadUserForSection(section) {
            const baseUser = this.cloneUserState(this.savedUser || this.user);

            if (section === 'all') {
                return this.cloneUserState(this.user);
            }

            this.getSectionFields(section).forEach((field) => {
                baseUser[field] = this.cloneFieldValue(this.user[field]);
            });

            return baseUser;
        },
        buildUserFormData(payloadUser, options = {}) {
            const includeAvatar = !!options.includeAvatar;
            const section = options.section || 'all';
            const formData = new FormData();

            formData.append('_method', 'PUT');
            formData.append('id', this.$route.params.id);
            formData.append('section', section);

            if (section === 'all' || section === 'details') {
                formData.append('name', payloadUser.name || '');
                formData.append('email', payloadUser.email || '');
                formData.append('salutation', payloadUser.salutation || '');
                formData.append('country', payloadUser.country || '');
                formData.append('city', payloadUser.city || '');
                formData.append('postal_code', payloadUser.postal_code || '');
                formData.append('street', payloadUser.street || '');
                formData.append('house_number', payloadUser.house_number || '');
                formData.append('phone_landline', payloadUser.phone_landline || '');
                formData.append('phone_mobile', payloadUser.phone_mobile || '');
                formData.append('linkedin_url', payloadUser.linkedin_url || '');
                formData.append('xing_url', payloadUser.xing_url || '');
                formData.append('instagram_url', payloadUser.instagram_url || '');
                formData.append('facebook_url', payloadUser.facebook_url || '');
            }

            if (section === 'all' || section === 'password_login') {
                if (payloadUser.is_primary_admin || payloadUser.password_login_available) {
                    formData.append('password_login_enabled', payloadUser.password_login_enabled ? '1' : '0');
                }
            }

            if (section === 'all' || section === 'settings') {
                const isServiceProviderPayloadRole = this.isServiceProviderRoleForRoleId(payloadUser.role_id);
                const payloadRole = this.userRoles.find((item) => String(item.id) === String(payloadUser.role_id));
                const payloadRoleGroup = payloadRole ? deriveRoleGroup(payloadRole) : '';

                formData.append('role_id', payloadUser.role_id || '');
                formData.append('status', payloadUser.status ? '1' : '0');

                if (['customer', 'service_provider'].includes(payloadRoleGroup)) {
                    const companyAssignment = normalizeUserCompanyAssignment(
                        payloadRoleGroup,
                        payloadUser.company_id,
                        payloadUser.company_ids,
                        this.companies,
                        payloadUser.company_assignments
                    );

                    formData.append('company_id', companyAssignment.companyId || '');
                    companyAssignment.companyIds.forEach((companyId, index) => {
                        formData.append(`company_ids[${index}]`, String(companyId));
                    });
                    companyAssignment.companyAssignments.forEach((assignment, index) => {
                        Object.entries(assignment).forEach(([key, value]) => {
                            formData.append(
                                `company_assignments[${index}][${key}]`,
                                typeof value === 'boolean' ? (value ? '1' : '0') : String(value)
                            );
                        });
                    });
                }
                if (this.isCustomerPositionsFeatureVisibleForRole(payloadUser.role_id)) {
                    formData.append('customer_positions_enabled', (isServiceProviderPayloadRole || payloadUser.customer_positions_enabled) ? '1' : '0');
                }
                if (this.isCustomerTimelineFeatureVisibleForRole(payloadUser.role_id)) {
                    formData.append('customer_timeline_enabled', (isServiceProviderPayloadRole || payloadUser.customer_timeline_enabled) ? '1' : '0');
                }
                formData.append('customer_file_manager_enabled', payloadUser.customer_file_manager_enabled ? '1' : '0');
                formData.append('customer_documentations_enabled', payloadUser.customer_documentations_enabled ? '1' : '0');
                if (this.isBoardsFeatureVisibleForRole(payloadUser.role_id)) {
                    formData.append('customer_boards_enabled', payloadUser.customer_boards_enabled ? '1' : '0');
                }
                if (this.isRelationshipsFeatureVisibleForRole(payloadUser.role_id)) {
                    formData.append('customer_relationships_enabled', payloadUser.customer_relationships_enabled ? '1' : '0');
                }
                formData.append(
                    'relationships_contact_visible_to_customers',
                    payloadUser.relationships_contact_visible_to_customers ? '1' : '0'
                );
                formData.append(
                    'relationships_contact_visible_to_employees',
                    payloadUser.relationships_contact_visible_to_employees ? '1' : '0'
                );
                formData.append(
                    'relationships_contact_visible_to_service_providers',
                    payloadUser.relationships_contact_visible_to_service_providers ? '1' : '0'
                );
                if (this.isCustomerFinancesFeatureVisibleForRole(payloadUser.role_id)) {
                    formData.append('customer_finances_enabled', (isServiceProviderPayloadRole || payloadUser.customer_finances_enabled) ? '1' : '0');
                }
                if (this.isCmsFeatureVisibleForRole(payloadUser.role_id)) {
                    formData.append('customer_cms_enabled', payloadUser.customer_cms_enabled ? '1' : '0');
                }
                formData.append('customer_server_monitoring_enabled', payloadUser.customer_server_monitoring_enabled ? '1' : '0');
                formData.append('customer_vault_enabled', payloadUser.customer_vault_enabled ? '1' : '0');

                if (this.isProjectAssignableForRole(payloadUser.role_id)) {
                    this.normalizeProjectIds(payloadUser.project_ids).forEach((projectId, index) => {
                        formData.append(`project_ids[${index}]`, String(projectId));
                    });
                }
            }

            if (section === 'all' || section === 'ai') {
                formData.append('ai_enabled', payloadUser.ai_enabled ? '1' : '0');
            }

            if (section === 'all' || section === 'storage_limit') {
                formData.append('role_id', payloadUser.role_id || '');
                formData.append('customer_file_manager_enabled', payloadUser.customer_file_manager_enabled ? '1' : '0');
                formData.append('file_manager_storage_limit_mb', this.resolveStorageLimitPayloadValue(payloadUser));
            }

            if (section === 'all' || section === 'test_account') {
                formData.append('is_test_account', payloadUser.is_test_account ? '1' : '0');
            }

            if (section === 'all') {
                this.appendNotificationSettings(formData);
            }

            if (includeAvatar) {
                if (this.avatarRemoved) {
                    formData.append('gravatar', '1');
                } else if (this.user.avatar instanceof File) {
                    formData.append('avatar', this.user.avatar);
                }
            }

            return formData;
        },
        buildNotificationSettingsFormData() {
            const formData = new FormData();

            formData.append('_method', 'PUT');
            formData.append('id', this.$route.params.id);
            formData.append('section', 'notification_settings');

            return this.appendNotificationSettings(formData);
        },
        appendNotificationSettings(formData) {

            this.notificationSettings.forEach((item) => {
                if (!item || !item.key) {
                    return;
                }

                formData.append(`preferences[${item.key}]`, item.enabled ? '1' : '0');
            });

            return formData;
        },
        getSectionFields(section) {
            const fields = {
                details: [
                    'name',
                    'email',
                    'salutation',
                    'country',
                    'city',
                    'postal_code',
                    'street',
                    'house_number',
                    'phone_landline',
                    'phone_mobile',
                    'linkedin_url',
                    'xing_url',
                    'instagram_url',
                    'facebook_url',
                    'avatar',
                    'gravatar',
                ],
                password_login: ['password_login_enabled'],
                settings: [
                    'role_id',
                    'company_id',
                    'company_ids',
                    'company_assignments',
                    'project_ids',
                    'status',
                    'customer_positions_enabled',
                    'customer_timeline_enabled',
                    'customer_file_manager_enabled',
                    'customer_documentations_enabled',
                    'customer_boards_enabled',
                    'customer_relationships_enabled',
                    'relationships_contact_visible_to_customers',
                    'relationships_contact_visible_to_employees',
                    'relationships_contact_visible_to_service_providers',
                    'customer_finances_enabled',
                    'customer_server_monitoring_enabled',
                    'customer_vault_enabled',
                    'customer_cms_enabled',
                ],
                ai: ['ai_enabled'],
                storage_limit: ['file_manager_storage_limit_mb'],
                test_account: ['is_test_account'],
                notification_settings: [],
            };

            return fields[section] || [];
        },
        cloneUserState(user) {
            return {
                ...user,
                company_id: user.company_id === null ? null : Number(user.company_id),
                company_ids: Array.isArray(user.company_ids) ? [...user.company_ids] : [],
                company_assignments: Array.isArray(user.company_assignments)
                    ? user.company_assignments.map((assignment) => ({...assignment}))
                    : [],
                project_ids: this.normalizeProjectIds(user.project_ids),
            };
        },
        cloneFieldValue(value) {
            if (Array.isArray(value)) {
                return value.map((item) => {
                    if (item && typeof item === 'object') {
                        return {...item};
                    }

                    return item;
                });
            }

            return value;
        },
        normalizeNotificationSettings(items) {
            if (!Array.isArray(items)) {
                return [];
            }

            return items
                .filter((item) => item && typeof item === 'object' && item.key)
                .map((item) => ({
                    key: item.key || '',
                    name: item.name || item.key || '',
                    name_translation_key: item.name_translation_key || '',
                    module: item.module || '',
                    module_translation_key: item.module_translation_key || '',
                    enabled: item.enabled !== false,
                }));
        },
        cloneNotificationSettings(items) {
            return this.normalizeNotificationSettings(items).map((item) => ({...item}));
        },
        isProjectAssignableForRole(roleId) {
            const role = this.userRoles.find((item) => String(item.id) === String(roleId));
            if (!role) {
                return false;
            }

            return ['customer', 'employee', 'service_provider', 'guest'].includes(deriveRoleGroup(role));
        },
        selectAvatar() {
            this.$refs.changeAvatar.click();
        },
        removeAvatar() {
            this.avatarRemoved = true;
            this.user.avatar = this.user.gravatar;
            this.user.avatar_preview = null;
            if (this.$refs.changeAvatar) {
                this.$refs.changeAvatar.value = '';
            }
        },
        isSupportedAvatarFile(file) {
            return ['image/png', 'image/x-citrix-png', 'image/x-png', 'image/jpeg', 'image/x-citrix-jpeg'].includes(file.type);
        },
        replaceLaravelPlaceholders(text, params = {}) {
            let value = String(text || '');
            Object.keys(params).forEach((key) => {
                value = value.replace(new RegExp(':' + key + '\\b', 'g'), String(params[key] ?? ''));
            });
            return value;
        },
        translateLaravelMessage(key, params = {}) {
            return this.replaceLaravelPlaceholders(this.$i18n.t(key).toString(), params);
        },
        validateAvatarFile(file) {
            const self = this;

            return new Promise(function (resolve) {
                const objectUrl = URL.createObjectURL(file);
                const image = new Image();

                image.onload = function () {
                    const width = image.naturalWidth;
                    const height = image.naturalHeight;

                    URL.revokeObjectURL(objectUrl);

                    if (width !== height) {
                        resolve(self.translateLaravelMessage('The :attribute must be square', {
                            attribute: self.$i18n.t('Avatar').toString(),
                        }));
                        return;
                    }

                    if (width > 1000 || height > 1000) {
                        resolve(self.translateLaravelMessage('The :attribute may not be greater than :width x :height pixels', {
                            attribute: self.$i18n.t('Avatar').toString(),
                            width: 1000,
                            height: 1000,
                        }));
                        return;
                    }

                    resolve(null);
                };

                image.onerror = function () {
                    URL.revokeObjectURL(objectUrl);
                    resolve(self.translateLaravelMessage('The file has to be an image', {
                        attribute: self.$i18n.t('Avatar').toString(),
                    }));
                };

                image.src = objectUrl;
            });
        },
        changeAvatar(event) {
            const self = this;
            if (!event.target.files.length) {
                return;
            }

            const file = event.target.files[0];
            if (!self.isSupportedAvatarFile(file)) {
                self.$notify({
                    title: self.$i18n.t('Unsupported file type').toString(),
                    text: self.$i18n.t('Only image type files are accepted').toString(),
                    type: 'error'
                });
                event.target.value = '';
                return;
            }

            self.validateAvatarFile(file).then(function (errorText) {
                if (errorText) {
                    self.$notify({
                        title: self.$i18n.t('Error').toString(),
                        text: errorText,
                        type: 'error'
                    });
                    event.target.value = '';
                    return;
                }

                self.avatarRemoved = false;
                self.user.avatar = file;
                self.user.avatar_preview = URL.createObjectURL(file);
            });
        },
        changePassword() {
            const self = this;
            self.passwordLoading = true;
            axios.post('api/workspace/admin/users/' + self.$route.params.id + '/password', {
                password: self.user.password,
                password_confirmation: self.user.password_confirmation,
            }).then(function () {
                self.passwordLoading = false;
                self.user.password = null;
                self.user.password_confirmation = null;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Password changed successfully').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.passwordLoading = false;
            });
        },
        normalizeHourlyRate(value) {
            if (value === null || value === undefined || value === '') {
                return null;
            }

            const normalized = String(value).replace(',', '.').trim();
            if (normalized === '') {
                return null;
            }

            return normalized;
        },
        normalizeStorageLimit(value) {
            if (value === null || value === undefined || value === '') {
                return null;
            }

            const normalized = Number(value);
            if (!Number.isFinite(normalized) || normalized <= 0) {
                return null;
            }

            const normalizedInt = Math.floor(normalized);
            const allowed = [500, 1024, 10240, 51200];
            if (!allowed.includes(normalizedInt)) {
                return null;
            }

            return normalizedInt;
        },
        resolveStorageLimitPayloadValue(payloadUser = this.user) {
            if (this.isAdminRoleSelectedForRole(payloadUser.role_id) || !payloadUser.customer_file_manager_enabled || !this.isStorageLimitRoleForRole(payloadUser.role_id)) {
                return '';
            }

            const normalized = this.normalizeStorageLimit(payloadUser.file_manager_storage_limit_mb);
            return normalized === null ? '' : String(normalized);
        },
        isAdminRoleSelectedForRole(roleId) {
            const role = this.userRoles.find((item) => String(item.id) === String(roleId));
            if (!role) {
                return false;
            }

            return deriveRoleGroup(role) === 'admin';
        },
        isCustomerRoleForRole(roleId) {
            const role = this.userRoles.find((item) => String(item.id) === String(roleId));
            if (!role) {
                return false;
            }

            return deriveRoleGroup(role) === 'customer';
        },
        isStorageLimitRoleForRole(roleId) {
            const role = this.userRoles.find((item) => String(item.id) === String(roleId));
            if (!role) {
                return false;
            }

            return ['customer', 'guest'].includes(deriveRoleGroup(role));
        },
        deleteUser() {
            if (!this.canDeleteUser || this.deleteLoading) {
                return;
            }

            const self = this;
            self.deleteLoading = true;
            axios.delete('api/workspace/admin/users/' + self.$route.params.id).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                if (self.embedded) {
                    self.$emit('deleted', Number(self.$route.params.id));
                    self.deleteUserModal = false;
                    return;
                }

                self.$router.push(self.usersListPath);
            }).catch(function (error) {
                if (error.response && error.response.status === 422 && error.response.data && error.response.data.delete_guard) {
                    self.user.delete_guard = error.response.data.delete_guard;
                    self.savedUser = self.savedUser ? {
                        ...self.savedUser,
                        delete_guard: error.response.data.delete_guard,
                    } : self.savedUser;
                }
            }).finally(function () {
                self.deleteLoading = false;
            });
        },
        openDeleteUserModal() {
            this.deleteUserModal = true;
        },
        getUserRoles() {
            const self = this;
            return axios.get('api/workspace/admin/users/user-roles').then(function (response) {
                self.userRoles = response.data;
            }).catch(function () {
                self.userRoles = [];
                self.loadingState = null;
            });
        },
        getProjects() {
            const self = this;
            axios.get('api/workspace/admin/users/projects').then(function (response) {
                self.projects = Array.isArray(response.data) ? response.data : [];
            }).catch(function () {
                self.projects = [];
            });
        },
        getCompanies() {
            const self = this;
            return axios.get('api/workspace/admin/users/companies').then(function (response) {
                self.companies = Array.isArray(response.data) ? response.data : [];
            }).catch(function () {
                self.companies = [];
            });
        },
        normalizeProjectIds(value) {
            if (!Array.isArray(value)) {
                return [];
            }

            return value
                .map((projectId) => Number(projectId))
                .filter((projectId) => Number.isInteger(projectId) && projectId > 0)
                .filter((projectId, index, list) => list.indexOf(projectId) === index);
        },
        translateTemplateLabel(key, fallback) {
            const normalizedFallback = typeof fallback === 'string' ? fallback : '';
            if (!key || typeof key !== 'string') {
                return normalizedFallback;
            }

            const translated = this.$t(key);
            if (typeof translated === 'string' && translated !== '' && translated !== key) {
                return translated;
            }

            return normalizedFallback;
        },
    }
}
</script>

<style lang="scss">
@reference "../../../../../../sass/tailwind-reference.css";

.avatar-editor {
    .avatar-editor-remove {
        @apply hidden;
    }

    &:hover {
        .avatar-editor-remove {
            @apply inline-flex;
        }
    }
}

.user-account-edit-embedded {
    height: 100%;
    background: #fff;
    overscroll-behavior: contain;

    .max-w-7xl {
        max-width: none;
        padding-left: 0 !important;
        padding-right: 0 !important;
    }

    .mt-6.shadow {
        margin-top: 0;
        border-radius: 0;
        box-shadow: none;
    }

    .user-account-edit-section {
        display: block;
        padding: 1.5rem 1.75rem;
        border-top: 1px solid var(--border-ultralight);
    }

    .user-account-edit-toolbar + form .user-account-edit-section {
        border-top: 0;
    }

    .user-account-edit-section > div + div {
        margin-top: 1.25rem;
    }

    .user-account-editor-footer {
        margin-top: 0;
    }
}

.user-account-edit-toolbar {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    min-height: 3.25rem;
    align-items: stretch;
    border-bottom: 1px solid var(--border-ultralight);
    background: var(--ui-surface-raised);
}

.user-account-edit-toolbar-title {
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    padding: 0 1rem;
    color: var(--ui-text-strong);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
}

.user-account-edit-save {
    width: 8rem;
    min-height: 3.25rem;
    flex: 0 0 8rem;
    border-radius: 0;
}

.user-account-edit-close {
    display: none;
    width: 3.25rem;
    flex: 0 0 3.25rem;
    align-items: center;
    justify-content: center;
    border: 0;
    border-right: 1px solid var(--border-ultralight);
    background: transparent;
    color: var(--ui-text-subtle);
}

.user-account-edit-close:focus {
    outline: none;
}

.user-account-editor-footer {
    display: flex;
    height: 3.5rem;
    min-height: 3.5rem;
    align-items: stretch;
    justify-content: space-between;
    gap: 1rem;
    overflow: hidden;
    padding: 0 1.5rem;
    border-radius: 0 0 0.125rem 0.125rem;
    background: var(--ui-surface-raised);
    box-shadow: inset 0 1px 0 var(--border-ultralight);
}

.user-account-editor-footer-wrap {
    position: sticky;
    bottom: 0;
    z-index: 20;
}

.user-account-editor-updated {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    gap: 0.375rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.user-account-editor-updated-avatar {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
    border-radius: 9999px;
    object-fit: cover;
}

.user-account-editor-delete {
    flex-shrink: 0;
    color: var(--ui-text-subtle);
}

.user-account-editor-delete:hover,
.user-account-editor-delete:focus {
    color: var(--ui-text-default);
}

@media (max-width: 1279px) {
    .user-account-edit-close {
        display: inline-flex;
    }
}
</style>
